import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import comms from '../../comms';
import { getDirections } from '../../../services/directionsService';
import {
  sendGetFlagTripRates,
  sendQuoteFlagTrip,
  sendCreateItineraryFlagTrip,
} from './itinerarySocketHelper';

/**
 * FlagTripCreateModal — Phase B itinerary-mode flag trip.
 *
 * Pickup = driver's current GPS (passenger already on board → trip created Loaded).
 * Dropoff = a Google Places address the driver types. The driver picks capacity and a
 * rate; the app asks the server for a price + route-fit check (hard gate), then submits.
 *
 * Server contract (see itinerary-flag-trip-phaseB.md): three ITINERARY_ACTION senders
 * (rates / quote / create) with replies on flagTripRates, flagTripQuote, flagTripCreated.
 */

// current GPS → dropoff travel distance/time. Google Directions preferred; OSRM fallback
// via the app's getDirections (which returns distance in meters, duration in seconds).
async function computeMileageAndDuration(origin, dest, fleetSettings) {
  try {
    if (window.google?.maps?.DirectionsService) {
      const svc = new window.google.maps.DirectionsService();
      // Cap the Directions call so a hung/denied request can't freeze the modal
      // on "Checking…" — fall through to OSRM / a 0 result instead.
      const res = await Promise.race([
        svc.route({
          origin: { lat: origin.lat, lng: origin.lng },
          destination: { lat: dest.lat, lng: dest.lon },
          travelMode: window.google.maps.TravelMode.DRIVING,
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('directions timeout')), 8000)),
      ]);
      const leg = res?.routes?.[0]?.legs?.[0];
      if (leg) {
        return {
          mileage: (leg.distance?.value || 0) / 1609.344,
          durationSeconds: leg.duration?.value || 0,
        };
      }
    }
  } catch (e) {
    /* fall through to OSRM */
  }
  try {
    const r = await getDirections({
      origin: { lat: origin.lat, lng: origin.lng },
      destination: { lat: dest.lat, lng: dest.lon },
      waypoints: [],
      osrmUrl: fleetSettings?.osrmUrl || fleetSettings?.osrmBaseUrl || fleetSettings?.OsrmBaseUrl || null,
    });
    if (r) {
      // getDirections returns { distance (meters), duration (seconds) }
      return { mileage: (r.distance || 0) / 1609.344, durationSeconds: r.duration || 0 };
    }
  } catch (e) {
    /* give up — server defaults dropoff to pickup + 15 min if duration is 0 */
  }
  return { mileage: 0, durationSeconds: 0 };
}

// The dispatch server sometimes relays a low-level parse error: its pricing handler
// received an HTML error page from an upstream pricing/routing service and tried to
// JSON.parse it (e.g. "Unexpected token < in JSON at position 4"). Show the driver
// something actionable instead of that raw developer text — the full payload is
// logged to the console for the backend team.
function friendlyServerError(raw, fallback) {
  const msg = String(raw || '').trim();
  if (!msg) return fallback;
  if (/unexpected token|in json at position|<!doctype|<html|syntaxerror/i.test(msg)) {
    return 'Pricing is temporarily unavailable (server error). Please try again in a moment, or contact dispatch.';
  }
  return msg;
}

const FlagTripCreateModal = ({
  isOpen,
  onClose,
  routeId,
  vehicleId,
  driverId,
  currentLocation, // { lat, lng } — pickup origin
  fleetSettings,
  googleMapsLoaded,
  onSuccess,
}) => {
  const [destination, setDestination] = useState(null); // {address,lat,lon,streetNumber,...}
  const [ambulatoryCount, setAmbulatoryCount] = useState(1);
  const [wheelchairCount, setWheelchairCount] = useState(0);
  const [rates, setRates] = useState([]);
  const [selectedRateId, setSelectedRateId] = useState('');
  const [quote, setQuote] = useState(null); // { success, allowable, price, dropoffTime, mileage, error }
  const [travel, setTravel] = useState({ mileage: 0, durationSeconds: 0 });
  const [isQuoting, setIsQuoting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [rawServerError, setRawServerError] = useState(null); // raw server payload, surfaced under a debug flag
  const [resolvedOrigin, setResolvedOrigin] = useState(null); // self-acquired GPS fix when the parent has none yet
  const [originStatus, setOriginStatus] = useState('idle'); // 'idle' | 'locating' | 'ready' | 'error'

  const acRef = useRef(null);
  // localStorage.flagTripDebug === '1' surfaces the raw server error in the modal so
  // it can be screenshotted/relayed to the backend team without opening DevTools.
  let debugOn = false;
  try { debugOn = localStorage.getItem('flagTripDebug') === '1'; } catch (e) { debugOn = false; }
  const lastSubmitRef = useRef(0);
  const SUBMIT_COOLDOWN_MS = 5000;

  // Reset whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setDestination(null);
    setAmbulatoryCount(1);
    setWheelchairCount(0);
    setSelectedRateId('');
    setQuote(null);
    setTravel({ mileage: 0, durationSeconds: 0 });
    setIsQuoting(false);
    setIsSubmitting(false);
    setError(null);
    setRawServerError(null);
    setResolvedOrigin(null);
    setOriginStatus('idle');
  }, [isOpen]);

  // Pickup origin = the driver's live location. Prefer the parent's vehiclePosition;
  // if it isn't available yet (GPS still acquiring, or testing on desktop), acquire a
  // one-time fix here so the price/fit check isn't blocked.
  useEffect(() => {
    if (!isOpen) return;
    if (currentLocation && currentLocation.lat != null && currentLocation.lng != null) {
      setOriginStatus('ready');
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setOriginStatus('error');
      return;
    }
    setOriginStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setResolvedOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setOriginStatus('ready');
      },
      () => setOriginStatus('error'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 }
    );
  }, [isOpen, currentLocation]);

  // Effective pickup origin: parent GPS first, then the self-acquired fix.
  const origin =
    currentLocation && currentLocation.lat != null && currentLocation.lng != null
      ? currentLocation
      : resolvedOrigin;

  // Subscribe to the server reply events while open; request the rate list on open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const socket = comms.getSocket();
    if (!socket) {
      setError('Not connected to dispatch. Try again in a moment.');
      return undefined;
    }
    const logRaw = (label, payload) => {
      console.error(label, payload);
      try { setRawServerError(`${label}\n${JSON.stringify(payload, null, 2)}`); }
      catch (err) { setRawServerError(`${label}\n${String(payload)}`); }
    };
    const onRates = (r) => {
      if (r?.success) setRates(Array.isArray(r.rates) ? r.rates : []);
      else {
        logRaw('[FlagTrip] flagTripRates error payload:', r);
        setError(friendlyServerError(r?.error, 'Could not load rates.'));
      }
    };
    const onQuote = (q) => {
      setIsQuoting(false);
      setQuote(q || null);
      if (q && q.success === false) {
        logRaw('[FlagTrip] flagTripQuote error payload:', q);
        setError(friendlyServerError(q.error, 'Pricing failed.'));
      }
    };
    const onCreated = (c) => {
      setIsSubmitting(false);
      if (c?.success) {
        if (onSuccess) onSuccess(c);
        onClose();
      } else {
        logRaw('[FlagTrip] flagTripCreated error payload:', c);
        setError(friendlyServerError(c?.error, 'Could not create the trip.'));
      }
    };
    const onItinError = (e) => {
      if (e?.action === 'createItineraryFlagTrip') {
        setIsSubmitting(false);
        logRaw('[FlagTrip] itinerary_error (create):', e);
        setError(friendlyServerError(e?.error, 'Could not create the trip.'));
      } else if (e?.action === 'quoteFlagTrip') {
        setIsQuoting(false);
        logRaw('[FlagTrip] itinerary_error (quote):', e);
        setError(friendlyServerError(e?.error, 'Could not price the trip.'));
      }
    };

    socket.on('flagTripRates', onRates);
    socket.on('flagTripQuote', onQuote);
    socket.on('flagTripCreated', onCreated);
    socket.on('itinerary_error', onItinError);

    sendGetFlagTripRates();

    return () => {
      socket.off('flagTripRates', onRates);
      socket.off('flagTripQuote', onQuote);
      socket.off('flagTripCreated', onCreated);
      socket.off('itinerary_error', onItinError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onPlaceChanged = () => {
    const place = acRef.current?.getPlace();
    if (!place?.geometry) return;
    const comp = (type) =>
      place.address_components?.find((c) => c.types.includes(type))?.short_name || '';
    setDestination({
      address: place.formatted_address || '',
      lat: place.geometry.location.lat(),
      lon: place.geometry.location.lng(),
      streetNumber: comp('street_number'),
      streetName: comp('route'),
      city: comp('locality') || comp('sublocality') || comp('administrative_area_level_2'),
      state: comp('administrative_area_level_1'),
      postalCode: comp('postal_code'),
    });
    setQuote(null); // invalidate any prior quote
    setError(null);
  };

  const canQuote =
    !!destination &&
    !!selectedRateId &&
    !!origin &&
    origin.lat != null &&
    origin.lng != null &&
    !isQuoting;

  const handleCheckPrice = async () => {
    if (!canQuote) return;
    setError(null);
    setQuote(null);
    setIsQuoting(true);
    try {
      const t = await computeMileageAndDuration(origin, destination, fleetSettings);
      setTravel(t);
      sendQuoteFlagTrip({
        routeId,
        rateId: selectedRateId,
        mileage: t.mileage,
        durationSeconds: t.durationSeconds,
        originLat: origin.lat,
        originLon: origin.lng,
        destinationLat: destination.lat,
        destinationLon: destination.lon,
      });
    } catch (e) {
      setIsQuoting(false);
      setError('Could not compute the route. Try again.');
    }
  };

  // Hard gate (per spec): only when priced AND fits.
  const canSubmit =
    !!quote &&
    quote.success === true &&
    quote.allowable === true &&
    typeof quote.price === 'number' &&
    !isSubmitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (!routeId || !vehicleId) {
      setError('Vehicle or route not available.');
      return;
    }
    const now = Date.now();
    if (now - lastSubmitRef.current < SUBMIT_COOLDOWN_MS) {
      setError('Please wait a moment before submitting again.');
      return;
    }
    lastSubmitRef.current = now;
    setIsSubmitting(true);
    setError(null);
    sendCreateItineraryFlagTrip({
      routeId,
      vehicleId,
      driverId,
      rateId: selectedRateId,
      rateName: rates.find((r) => r.id === selectedRateId)?.name,
      ambulatoryCount: parseInt(ambulatoryCount, 10) || 0,
      wheelchairCount: parseInt(wheelchairCount, 10) || 0,
      passengerName: 'Flag Trip',
      originLat: origin.lat,
      originLon: origin.lng,
      originTime: new Date().toISOString(),
      destinationLat: destination.lat,
      destinationLon: destination.lon,
      destinationStreetNumber: destination.streetNumber,
      destinationStreetName: destination.streetName,
      destinationCity: destination.city,
      destinationState: destination.state,
      destinationPostalCode: destination.postalCode,
      durationSeconds: travel.durationSeconds,
      mileage: travel.mileage,
      tripPrice: quote.price,
    });
  };

  if (!isOpen) return null;

  const stepBtn =
    'w-12 h-12 rounded-full bg-amber-500 hover:bg-amber-600 flex items-center justify-center text-2xl font-bold text-white transition-colors disabled:opacity-50';

  return (
    <div className="fixed inset-0 z-[2200] flex items-center justify-center bg-black bg-opacity-50">
      <div className="m-4 w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between bg-amber-500 px-6 py-4">
          <h2 className="text-xl font-semibold text-white">Flag Trip</h2>
          <button onClick={onClose} disabled={isSubmitting} className="text-white hover:text-gray-200">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[70vh] space-y-4 overflow-y-auto p-5">
          {error && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
          )}
          {debugOn && rawServerError && (
            <pre className="max-h-44 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-900 p-2 text-[10px] leading-snug text-amber-200">
              {rawServerError}
            </pre>
          )}

          <p className="text-xs text-gray-500">
            Pickup is your current location (passenger on board). Enter the dropoff below.
          </p>

          {/* Dropoff address (Google Places) */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Dropoff address</label>
            {googleMapsLoaded && window.google?.maps?.places ? (
              <Autocomplete
                onLoad={(ac) => { acRef.current = ac; }}
                onPlaceChanged={onPlaceChanged}
                options={{
                  // Only fetch the fields we read in onPlaceChanged — keeps the
                  // Places call cheap and guarantees geometry is returned.
                  fields: ['geometry', 'formatted_address', 'address_components', 'name'],
                }}
              >
                <input
                  type="text"
                  placeholder="Search dropoff address"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </Autocomplete>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                Map not ready — address search unavailable.
              </div>
            )}
            {destination?.address && (
              <div className="mt-1 text-xs text-gray-600">{destination.address}</div>
            )}
          </div>

          {/* Capacity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Ambulatory</label>
              <div className="flex items-center justify-center gap-3">
                <button className={stepBtn} disabled={ambulatoryCount <= 0} onClick={() => setAmbulatoryCount((n) => Math.max(0, n - 1))}>-</button>
                <span className="w-8 text-center text-2xl font-bold">{ambulatoryCount}</span>
                <button className={stepBtn} onClick={() => setAmbulatoryCount((n) => n + 1)}>+</button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Wheelchair</label>
              <div className="flex items-center justify-center gap-3">
                <button className={stepBtn} disabled={wheelchairCount <= 0} onClick={() => setWheelchairCount((n) => Math.max(0, n - 1))}>-</button>
                <span className="w-8 text-center text-2xl font-bold">{wheelchairCount}</span>
                <button className={stepBtn} onClick={() => setWheelchairCount((n) => n + 1)}>+</button>
              </div>
            </div>
          </div>

          {/* Rate */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Rate</label>
            <select
              value={selectedRateId}
              onChange={(e) => { setSelectedRateId(e.target.value); setQuote(null); }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
            >
              <option value="">{rates.length ? 'Select a rate' : 'Loading rates…'}</option>
              {rates.map((r) => (
                <option key={r.id} value={r.id}>{r.name}{r.code ? ` (${r.code})` : ''}</option>
              ))}
            </select>
          </div>

          {/* Check price / fit */}
          <button
            onClick={handleCheckPrice}
            disabled={!canQuote}
            className="w-full rounded-lg border border-amber-500 px-4 py-2.5 text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-50 disabled:opacity-50"
          >
            {isQuoting ? 'Checking…' : 'Check price & fit'}
          </button>

          {/* Why the button is disabled (so it isn't a silent grey button) */}
          {!canQuote && !isQuoting && (
            <p className="-mt-2 text-center text-xs text-gray-500">
              {!destination
                ? 'Enter a dropoff address to continue.'
                : !selectedRateId
                ? 'Select a rate to continue.'
                : originStatus === 'locating'
                ? 'Getting your current location…'
                : 'Your location is unavailable — enable location access to price the trip.'}
            </p>
          )}

          {/* Quote result */}
          {quote && quote.success && quote.allowable && typeof quote.price === 'number' && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Price</span>
                <span className="text-lg font-bold">${Number(quote.price).toFixed(2)}</span>
              </div>
              {travel.mileage > 0 && (
                <div className="mt-1 text-xs text-green-700">
                  ~{travel.mileage.toFixed(1)} mi · fits before next pickup
                </div>
              )}
            </div>
          )}
          {quote && quote.success && quote.allowable === false && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {quote.error || 'This trip does not fit before the next scheduled pickup.'}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-lg bg-amber-500 px-6 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? 'Creating…' : 'Create trip'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlagTripCreateModal;
